import { ChevronDownIcon, ChevronRightIcon, ReceiptIcon } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { creditNotePdfPath, invoicePdfPath } from '@/api/endpoints'
import { useInvoices, useMe } from '@/api/queries'
import type { Invoice, InvoiceKind } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { PayInvoiceDialog } from '@/components/billing/PayInvoiceDialog'
import { PdfButton } from '@/components/billing/PdfButton'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { hasBilling } from '@/lib/access'
import { centsLabel } from '@/lib/billing'
import { formatAbsolute } from '@/lib/format'
import {
  INVOICE_KINDS,
  invoiceLabel,
  invoiceStatus,
  isPayable,
  kindLabel,
  lineSummary,
  openTarget,
} from '@/lib/invoices'
import { cn } from '@/lib/utils'
import { SELECT_CLASS } from '@/staff/shared'
import { useReadOnly } from '@/staff/hooks'

/** Stop chasing `?open=<id>` through older pages after this many extra pages. */
const MAX_OPEN_PAGES = 10

/**
 * The INVOICES page (`/app/invoices`, 2026-10-BILLING-IN-APP BI3): every Stripe invoice of the
 * account, newest first, with its credit notes nested under it. PDFs come through our API (an
 * authenticated fetch); no Stripe URL ever reaches the browser. Open invoices are highlighted and
 * can be paid with a chosen card; `?open=<id>` (the link in our emails) scrolls to and expands one.
 */
export function InvoicesPage() {
  const me = useMe()
  const location = useLocation()
  const readOnly = useReadOnly()
  const allowed = hasBilling({ role: me.data?.role, billing: me.data?.billing })
  const [kind, setKind] = useState<InvoiceKind | null>(null)
  const invoices = useInvoices(kind, allowed)
  const rows = invoices.data?.pages.flatMap((page) => page.invoices) ?? []
  const target = openTarget(location.search, rows)
  const wanted = new URLSearchParams(location.search).get('open')
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [paying, setPaying] = useState<Invoice | null>(null)
  const scrolled = useRef<string | null>(null)

  // The email link names an invoice that may be on an older page: fetch pages until it appears.
  useEffect(() => {
    if (wanted === null || target !== null) return
    if (invoices.hasNextPage && !invoices.isFetchingNextPage && (invoices.data?.pages.length ?? 0) <= MAX_OPEN_PAGES) {
      void invoices.fetchNextPage()
    }
  }, [wanted, target, invoices])

  useEffect(() => {
    if (target === null || scrolled.current === target) return
    scrolled.current = target
    setExpanded((current) => new Set(current).add(target))
    window.requestAnimationFrame(() => {
      document.getElementById(`invoice-${target}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }, [target])

  function toggle(id: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (me.isPending) {
    return (
      <>
        <PageHeader title="Invoices" />
        <PageBody>
          <TableSkeleton rows={4} columns={7} />
        </PageBody>
      </>
    )
  }

  if (!allowed) {
    return (
      <>
        <PageHeader title="Invoices" description="Invoices and credit notes for your account" />
        <PageBody>
          <Alert>
            <AlertTitle>Billing access only</AlertTitle>
            <AlertDescription>
              Invoices are visible to account administrators and members with billing access.
            </AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Invoices"
        description="Every invoice and credit note for this account. Download the PDF, or pay an open invoice with the card you choose."
        actions={
          <select
            aria-label="Filter by kind"
            data-testid="kind-filter"
            className={cn(SELECT_CLASS, 'w-44')}
            value={kind ?? 'all'}
            onChange={(event) => {
              const value = event.target.value
              setKind(value === 'all' ? null : (value as InvoiceKind))
            }}
          >
            {INVOICE_KINDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        }
      />
      <PageBody>
        {invoices.isError ? (
          <ErrorState
            error={invoices.error}
            title="Could not load invoices"
            onRetry={() => {
              void invoices.refetch()
            }}
          />
        ) : invoices.isPending ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={5} columns={7} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ReceiptIcon}
            title="No invoices yet"
            description={
              kind === null
                ? 'Invoices appear here after your first payment: a subscription, a top-up or an auto-recharge.'
                : 'No invoice of this kind.'
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <Table data-testid="invoices-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Refunded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((invoice) => {
                  const open = expanded.has(invoice.id)
                  const status = invoiceStatus(invoice.status)
                  const payable = isPayable(invoice)
                  return (
                    <Fragment key={invoice.id}>
                      <TableRow
                        id={`invoice-${invoice.id}`}
                        data-testid="invoice-row"
                        data-invoice-id={invoice.id}
                        data-status={invoice.status}
                        className={cn(
                          payable && 'bg-[color-mix(in_oklch,var(--destructive)_8%,transparent)]',
                          target === invoice.id && 'ring-2 ring-primary ring-inset',
                        )}
                      >
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={open ? 'Hide details' : 'Show details'}
                            aria-expanded={open}
                            onClick={() => {
                              toggle(invoice.id)
                            }}
                          >
                            {open ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{invoiceLabel(invoice)}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{formatAbsolute(invoice.created)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{kindLabel(invoice.kind)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.tone} data-testid="invoice-status">
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{centsLabel(invoice.subtotal_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums">{centsLabel(invoice.tax_cents)}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {centsLabel(invoice.total_cents)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {invoice.amount_refunded_cents > 0 ? centsLabel(invoice.amount_refunded_cents) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            {payable && !readOnly ? (
                              <Button
                                size="sm"
                                data-testid="pay-now"
                                onClick={() => {
                                  setPaying(invoice)
                                }}
                              >
                                Pay now
                              </Button>
                            ) : null}
                            <PdfButton
                              path={invoicePdfPath(invoice.id)}
                              fallbackName={`${invoiceLabel(invoice)}.pdf`}
                              testId="download-invoice"
                            />
                          </span>
                        </TableCell>
                      </TableRow>
                      {open ? (
                        <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid="invoice-detail">
                          <TableCell />
                          <TableCell colSpan={9} className="whitespace-normal">
                            <div className="flex flex-col gap-3 py-1 text-sm">
                              <p className="text-muted-foreground">
                                {lineSummary(invoice) || 'No line items.'}
                                {invoice.period_start !== null && invoice.period_end !== null
                                  ? ` · ${formatAbsolute(invoice.period_start)} to ${formatAbsolute(invoice.period_end)}`
                                  : ''}
                              </p>
                              {invoice.lines.length > 0 ? (
                                <ul className="divide-y divide-border rounded-md border border-border bg-card">
                                  {invoice.lines.map((line, index) => (
                                    <li key={index} className="flex justify-between gap-4 px-3 py-1.5">
                                      <span>
                                        {line.description ?? 'Item'}
                                        {line.quantity !== null && line.quantity > 1 ? ` × ${line.quantity}` : ''}
                                      </span>
                                      <span className="tabular-nums">{centsLabel(line.amount_cents)}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              {invoice.status === 'open' ? (
                                <p className="text-destructive">{centsLabel(invoice.amount_due_cents)} is due.</p>
                              ) : null}
                              {invoice.credit_notes.length > 0 ? (
                                <div className="flex flex-col gap-2" data-testid="credit-notes">
                                  <p className="text-xs font-medium text-muted-foreground uppercase">Credit notes</p>
                                  {invoice.credit_notes.map((note) => (
                                    <div
                                      key={note.id}
                                      data-testid="credit-note-row"
                                      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-card px-3 py-2"
                                    >
                                      <span className="font-mono text-xs">{note.number ?? note.id}</span>
                                      <span className="text-xs text-muted-foreground">{formatAbsolute(note.created)}</span>
                                      <span className="tabular-nums">{centsLabel(note.total_cents)}</span>
                                      {note.reason === null ? null : (
                                        <span className="text-xs text-muted-foreground">{note.reason}</span>
                                      )}
                                      <span className="ml-auto">
                                        <PdfButton
                                          path={creditNotePdfPath(note.id)}
                                          fallbackName={`${note.number ?? note.id}.pdf`}
                                          testId="download-credit-note"
                                        />
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {invoices.hasNextPage ? (
          <div className="flex justify-center">
            <Button
              variant="outline"
              disabled={invoices.isFetchingNextPage}
              data-testid="load-more-invoices"
              onClick={() => {
                void invoices.fetchNextPage()
              }}
            >
              {invoices.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
        {wanted !== null && target === null && !invoices.isPending && !invoices.hasNextPage ? (
          <Alert data-testid="invoice-not-found">
            <AlertDescription>That invoice is not in this list.</AlertDescription>
          </Alert>
        ) : null}
      </PageBody>
      {paying === null ? null : (
        <PayInvoiceDialog
          invoice={paying}
          onClose={() => {
            setPaying(null)
          }}
        />
      )}
    </>
  )
}
